import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SuperAdminOrderDetailsModalComponent } from '../components/super-admin-order-details-modal/super-admin-order-details-modal.component';
import { CancelOrderModalComponent } from '../../components/cancel-order-modal/cancel-order-modal.component';
import { OrderCancellationSuccessModalComponent } from '../../components/order-cancellation-success-modal/order-cancellation-success-modal.component';
import { NotificationService } from '../../services/notification.service';
import { CancellationService } from '../../services/cancellation.service';
import { OrderUtils } from '../../orders/utils/order.utils';
import { TableUtils, DragScrollConfig } from '../../orders/utils/table.utils';
import {
  mapAdminDashboardPeriodStats,
  ADMIN_DASHBOARD_PERIOD_STATS,
  ORDER_SHIMMER_COLUMNS,
  ADMIN_ORDER_FILTER_CATEGORIES,
  createDefaultAdminOrderFilters,
  syncPackageFilterCategoryOptions,
} from '../../orders/constants/orders.constants';
import {
  calendarDayInZone,
  formatZoneLong,
  formatZoneShort,
  getViewerZone,
} from '../utils/reporting-timezone.util';
import {
  buildPaginationData,
  calculateTotalPages,
  applyPageChange,
  applyItemsPerPageChange,
  runCancelOrderConfirm,
  openOrderDetailsModal,
  closeOrderDetailsModal,
} from '../../orders/utils/orders-page-shared.utils';
import { ApiService } from '../../services/api.service';
import { CompanyService } from '../services/company.service';
import {
  loadCompaniesAndSelectFirst,
  toggleCompanyDropdown,
  closeCompanyDropdown,
} from '../utils/company.util';
import { countActiveFilterValues } from '../utils/filter-count.util';
import { OrdersService } from '../../orders/services/orders.service';
import {
  Order,
  OrderStatus,
  OrderStats,
  ApiOrderStatistics,
} from '../../orders/models/order.models';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../../shared/components/empty-result/empty-result.component';
import { LoadingTableShimmerComponent } from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { StatsCardComponent } from '../../shared/components/stats-card/stats-card.component';
import { SearchbarComponent } from '../../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../../shared/components/action-button/action-button.component';
import {
  FilterModalComponent,
  FilterCategory,
} from '../../shared/components/filter-modal/filter-modal.component';
import {
  SimpleDropdownComponent,
  DropdownOption,
} from '../../shared/components/simple-dropdown/simple-dropdown.component';
import { formatDateToISO } from '../../shared/utils/date.util';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { provideNativeDateAdapter } from '@angular/material/core';
import { UsaPhoneDisplayPipe } from '../../shared/validators/usa-phone-display.pipe';

/**
 * Date window sent to the reporting endpoints: a server-owned preset name plus the plain
 * calendar dates that only a `custom` range needs.
 */
interface OrderDateWindow {
  filterBy: string;
  startDate?: string;
  endDate?: string;
}

/** Date-scope values the reporting endpoints accept as preset names as-is. */
const FILTER_MODAL_DATE_PRESETS = new Set([
  'today',
  'yesterday',
  'week',
  'month',
  'year',
  'custom',
]);

/**
 * Date-scope value meaning "send no date filter at all". Deliberately outside
 * `FILTER_MODAL_DATE_PRESETS` so it resolves to a null window and clears every date param.
 */
const ALL_TIME_DATE_VALUE = 'all';

/**
 * The one scope control that governs both the cards and the table. It lives in the toolbar
 * rather than the filter modal because it names the band above the cards: a period the page
 * announces cannot be picked somewhere the reader has to open a dialog to see.
 */
const DATE_SCOPE_OPTIONS: DropdownOption[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Week (Sun-Sat)', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Year', value: 'year' },
  { label: 'Custom Range', value: 'custom' },
  { label: 'All time', value: ALL_TIME_DATE_VALUE },
];

/** Band wording for each date scope. Custom ranges render their dates instead. */
const DATE_SCOPE_BAND_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This Week',
  month: 'This Month',
  year: 'This Year',
  custom: 'Custom Range',
  [ALL_TIME_DATE_VALUE]: 'All time',
};

/** Presets naming exactly one calendar day, so the band can spell that day out. */
const SINGLE_DAY_DATE_SCOPES = new Set(['today', 'yesterday']);

/** Band and column-header wording when the response covers several zones at once. */
const MULTI_ZONE_BAND_LABEL = 'per location';
const MULTI_ZONE_COLUMN_LABEL = 'local';

/**
 * Period wording for the empty state, phrased to sit inside a sentence. Absent for `all`, which
 * is the one scope whose emptiness is about the company rather than about the window.
 */
const DATE_SCOPE_EMPTY_STATE_PHRASES: Record<string, string> = {
  today: 'today',
  yesterday: 'yesterday',
  week: 'this week',
  month: 'this month',
  year: 'this year',
};

/**
 * Page size asked for when filling the location dropdown. The locations endpoint defaults to 25,
 * and that default is shared with the non-super-admin route so it cannot move; a truncated list
 * silently hides sites the super admin is meant to be able to select. A company with more sites
 * than this is still truncated — real pagination is the proper fix.
 */
const LOCATION_DROPDOWN_LIMIT = '500';

/**
 * CSV dropdown value to server-owned preset name. `all` is absent on purpose: it means
 * "send no date filter at all".
 */
const CSV_OPTION_DATE_PRESETS: Record<string, string> = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  lastWeek: 'last_week',
  thisMonth: 'this_month',
  lastMonth: 'last_month',
  thisYear: 'this_year',
  custom: 'custom',
};

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    SuperAdminOrderDetailsModalComponent,
    CancelOrderModalComponent,
    OrderCancellationSuccessModalComponent,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    TitleBarComponent,
    StatsCardComponent,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
    SimpleDropdownComponent,
    UsaPhoneDisplayPipe,
  ],
  // The custom date-range pickers need a date adapter; native suits these plain dates.
  providers: [provideNativeDateAdapter()],
  templateUrl: './admin-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-dashboard.component.scss'],
})
export class AdminDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(SuperAdminOrderDetailsModalComponent)
  orderDetailsModalComponent!: SuperAdminOrderDetailsModalComponent;
  @ViewChild('tableScrollWrapper') tableScrollWrapper?: ElementRef<HTMLElement>;

  searchTerm = '';
  itemsPerPage = 10;
  currentPage = 1;
  totalItems = 0;
  loading = true;

  // Company dropdown properties
  companyOptions: DropdownOption[] = [];
  selectedCompanyId = '';
  isCompanyDropdownOpen = false;

  // Location dropdown properties
  locationOptions: DropdownOption[] = [];
  selectedLocationId = '';
  isLocationDropdownOpen = false;

  // CSV download dropdown properties
  csvDownloadOptions: DropdownOption[] = [
    { label: 'All Orders', value: 'all' },
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week (Sun-Sat)', value: 'thisWeek' },
    { label: 'Last Week', value: 'lastWeek' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'This Year', value: 'thisYear' },
    { label: 'Custom Range', value: 'custom' },
  ];
  selectedCsvDownloadOption = '';
  isCsvDownloadDropdownOpen = false;
  csvStartDate: Date | null = null;
  csvEndDate: Date | null = null;
  isCalendarOpen = false;

  /** Exactly the seven window-scoped cards, in the order the band's period governs them. */
  orderStats: OrderStats[] = ADMIN_DASHBOARD_PERIOD_STATS.map(s => ({ ...s }));

  orders: Order[] = [];
  showFilterModal = false;

  // Date scope properties. The dropdown lives in the toolbar; only a custom range needs dates.
  readonly dateScopeOptions: DropdownOption[] = DATE_SCOPE_OPTIONS;
  dateFilterBy = 'today'; // 'today', 'yesterday', 'week', 'month', 'year', 'custom', 'all'
  isDateScopeDropdownOpen = false;
  dateStartDate: Date | null = null;
  dateEndDate: Date | null = null;

  /**
   * Wash type, status, source and package only. The date scope was promoted out of this modal:
   * it is the period the band announces, so it may not hide behind a dialog.
   */
  filterCategories: FilterCategory[] = ADMIN_ORDER_FILTER_CATEGORIES.filter(
    c => c.id !== 'paymentMode' && c.id !== 'discountApplied'
  ).map(c => ({
    ...c,
    options: c.options.map(o => ({ ...o })),
  }));

  shimmerColumns = ORDER_SHIMMER_COLUMNS;

  appliedFilters: any = this.createDefaultFilters();

  // Dropdown functionality
  openDropdownId: string | null = null;
  selectedOrderForDropdown: Order | null = null;

  // Order details modal
  showOrderDetailsModal = false;
  selectedOrder: Order | null = null;

  // Cancel order modal
  showCancelOrderModal = false;
  orderToCancel: Order | null = null;

  // Cancellation success modal
  showCancellationSuccessModal = false;
  cancellationRefundAmount = '';
  cancellationWasScheduled = false;

  // Sorting functionality
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | 'none' = 'none';

  // Drag scrolling configuration
  private dragScrollConfig!: DragScrollConfig;

  private readonly apiService = inject(ApiService);
  private readonly companyService = inject(CompanyService);
  private readonly ordersService = inject(OrdersService);
  private readonly messageService = inject(NotificationService);
  private readonly cancellationService = inject(CancellationService);
  private readonly http = inject(HttpClient);
  private destroy$ = new Subject<void>();
  /**
   * Timezone the statistics endpoint reports it bucketed by. The ONLY zone this screen may claim
   * in its audit label: a location's own zone is not it whenever the server buckets in one shared
   * zone, and the browser cannot tell which the server did.
   */
  private statsResolvedTimezone = '';
  /**
   * Every distinct zone the statistics response says it bucketed in. More than one means no single
   * label on this page can be true, so the period band, the scope line and the Date & Time column
   * all degrade together rather than quietly naming one of them.
   */
  private statsTimezones: string[] = [];
  /** Company-and-location scoped totals that ignore the date window. Null until the API answers. */
  private lifetimeOrders: number | null = null;
  private lifetimeRevenue: number | null = null;
  /** Super-admin has no shop-scoped package list API; keep empty so Package filter stays hidden. */
  private readonly filterPackages: { id: string; package_name: string; wash_types?: string[] }[] =
    [];

  ngOnInit(): void {
    this.loadCompanies();
    // Hide empty Package category (shop `view-car-wash-packages` is unauthorized for super-admin).
    this.refreshPackageFilterOptions([]);
  }

  private refreshPackageFilterOptions(selectedPackageIds?: string[]): void {
    this.filterCategories = syncPackageFilterCategoryOptions(
      this.filterCategories,
      this.filterPackages,
      selectedPackageIds
    );
  }

  ngAfterViewInit(): void {
    this.dragScrollConfig =
      TableUtils.reinitializeDragScrolling(this.tableScrollWrapper) ?? this.dragScrollConfig;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    TableUtils.cleanupTableScrolling();
  }

  private loadCompanies(): void {
    loadCompaniesAndSelectFirst(this.companyService, this.messageService, {
      setCompanyOptions: opts => {
        this.companyOptions = opts;
      },
      setSelectedCompanyId: id => {
        this.selectedCompanyId = id;
      },
      onFirstSelected: id => this.loadLocations(id),
      onComplete: () => {
        // If no companies are found, we stop loading here.
        // Otherwise, loading continues until loadOrders completes.
        if (this.companyOptions.length === 0) {
          this.loading = false;
        }
      },
    });
  }

  toggleCompanyDropdown(): void {
    toggleCompanyDropdown(this);
  }
  closeCompanyDropdown(): void {
    closeCompanyDropdown(this);
  }

  /**
   * Select a company by ID, update component state, reset dependent filters, and load or clear related data.
   * @example
   * electCompany('company123')
   * // void
   * @param {string} companyId - The ID of the company to select; pass a falsy value to clear the selection.
   * @returns {void} No return value; updates component state (selectedCompanyId, isCompanyDropdownOpen, currentPage, selectedLocationId, locationOptions, orders, totalItems) and triggers loading of locations when applicable.
   **/
  selectCompany(companyId: string): void {
    this.selectedCompanyId = companyId || '';
    this.isCompanyDropdownOpen = false;
    this.currentPage = 1;
    // Reset location when company changes
    this.selectedLocationId = '';
    this.locationOptions = [];
    this.statsResolvedTimezone = '';
    this.statsTimezones = [];
    this.lifetimeOrders = null;
    this.lifetimeRevenue = null;
    // Load locations for the selected company
    if (companyId) {
      this.loadLocations(companyId);
    } else {
      // Clear orders if no company selected
      this.orders = [];
      this.totalItems = 0;
    }
  }

  toggleLocationDropdown(): void {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
  }

  closeLocationDropdown(): void {
    this.isLocationDropdownOpen = false;
  }

  selectLocation(locationId: string): void {
    this.selectedLocationId = locationId || '';
    this.isLocationDropdownOpen = false;
    this.currentPage = 1;
    // Reload orders with new location filter
    if (this.selectedCompanyId) {
      this.loadOrders();
      this.loadOrderStats();
    }
  }

  /**
   * Load and initialize location options for a given company, select the first location if available, and trigger loading of orders and order statistics.
   * @example
   * loadLocations('company123')
   * void
   * @param {{string}} {{companyId}} - The ID of the company whose locations should be loaded.
   * @returns {{void}} Updates component state (locationOptions, selectedLocationId) and triggers loadOrders and loadOrderStats; does not return a value.
   **/
  private loadLocations(companyId: string): void {
    if (!companyId) {
      return;
    }

    this.apiService
      .getWithSuperAdminToken<any[]>(`admin/super-admin/companies/${companyId}/locations`, {
        limit: LOCATION_DROPDOWN_LIMIT,
      })
      .subscribe({
        next: response => {
          if (response.status && response.data) {
            // API returns data as an array directly
            const locations = Array.isArray(response.data) ? response.data : [];

            this.locationOptions = locations.map((location: any) => ({
              label: location.branch || location.name || location.id,
              value: location.id,
            }));

            // Select first location by default
            if (this.locationOptions.length > 0) {
              this.selectedLocationId = this.locationOptions[0].value;
              // Load orders and stats after selecting the first location
              this.loadOrders();
              this.loadOrderStats();
            } else {
              // No locations available, still try to load orders without location filter
              this.loadOrders();
              this.loadOrderStats();
            }
          }
        },
        error: error => {
          console.error('Error loading locations:', error);
          // Even if locations fail, try to load orders without location filter
          this.loadOrders();
          this.loadOrderStats();
        },
      });
  }

  /**
   * Load orders for the currently selected company applying pagination, search, location, date range, status, wash type and source filters; updates component state and handles API response/errors.
   * @example
   * loadOrders()
   * undefined
   * @returns {void} No return value; updates component state (orders, totalItems, loading) and shows error messages on failure.
   */
  private loadOrders(): void {
    if (!this.selectedCompanyId) {
      return;
    }

    this.loading = true;
    this.orders = [];
    this.totalItems = 0;

    const params: Record<string, any> = {
      page: this.currentPage.toString(),
      limit: this.itemsPerPage.toString(),
      ...this.buildSharedOrderParams(this.resolveFilterModalDateWindow()),
      ...this.buildFilterModalParams(),
    };

    this.apiService
      .getWithSuperAdminToken(
        `admin/super-admin/companies/${this.selectedCompanyId}/orders`,
        params
      )
      .subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            if (response.data.orders && Array.isArray(response.data.orders)) {
              this.orders = this.ordersService.mapApiOrdersToOrders(response.data.orders);
              this.totalItems =
                response.pagination?.total || response.data.totalOrders || this.orders.length;
            } else if (Array.isArray(response.data)) {
              this.orders = this.ordersService.mapApiOrdersToOrders(response.data);
              this.totalItems =
                response.total || response.totalItems || response.count || this.orders.length;
            }
          }
          this.loading = false;
          setTimeout(() => {
            this.dragScrollConfig =
              TableUtils.reinitializeDragScrolling(this.tableScrollWrapper) ??
              this.dragScrollConfig;
          }, 100);
        },
        error: error => {
          console.error('Error loading orders:', error);
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load orders',
          });
        },
      });
  }

  /**
   * Builds the filter-modal params (status, wash type, source, package) shared by the order list
   * and statistics requests, so the cards and the table always describe the same orders.
   * @example
   * buildFilterModalParams()
   * { order_status: 'completed', wash_types: 'single_wash,rewash' }
   * @returns {Record<string, string>} Params ready to merge into an endpoint-specific param map.
   */
  private buildFilterModalParams(): Record<string, string> {
    const params: Record<string, string> = {};

    const statusFilters: string[] = [];
    if (this.appliedFilters.status.completed) statusFilters.push('completed');
    if (this.appliedFilters.status.pending) statusFilters.push('pending');
    if (this.appliedFilters.status.canceled) statusFilters.push('cancelled');
    if (this.appliedFilters.status.scheduled) statusFilters.push('scheduled');
    if (this.appliedFilters.status.inProgress) statusFilters.push('inprogress');

    if (statusFilters.length > 0) {
      params['order_status'] = statusFilters.join(',');
    }

    const washTypeFilters: string[] = [];
    if (this.appliedFilters.carWashType.singleWash) washTypeFilters.push('single_wash');
    if (this.appliedFilters.carWashType.monthlyMembership)
      washTypeFilters.push('monthly_membership');
    if (this.appliedFilters.carWashType.prepaidWash) washTypeFilters.push('prepaid_wash_book');
    if (this.appliedFilters.carWashType.rewash) washTypeFilters.push('rewash');

    if (washTypeFilters.length === 1) {
      params['wash_type'] = washTypeFilters[0];
    } else if (washTypeFilters.length > 1) {
      params['wash_types'] = washTypeFilters.join(',');
    }

    const sourceFilters: string[] = [];
    if (this.appliedFilters.source?.wayCom) sourceFilters.push('way_web');
    if (this.appliedFilters.source?.scanAndGo) sourceFilters.push('scan_and_go');
    if (this.appliedFilters.source?.wayApp) sourceFilters.push('way_app');
    if (this.appliedFilters.source?.website) sourceFilters.push('web');

    if (sourceFilters.length > 0) {
      params['source'] = sourceFilters.join(',');
    }

    const packageIds: string[] = this.appliedFilters.packageIds ?? [];
    if (packageIds.length > 0) {
      params['package_ids'] = packageIds.join(',');
    }

    return params;
  }

  /**
   * Builds the query params shared by the order list, statistics and CSV endpoints so the three
   * can never disagree about what window they describe.
   *
   * No `timezone` param is sent, and none may be added back. The server resolves the reporting
   * zone from the location the window is scoped to; the only zone this browser could offer comes
   * from the shop-owner `shop_location_state` key, which the super-admin path never writes, so it
   * is either empty or an unrelated shop's zone. Sending it re-buckets every figure on the page
   * onto a day nothing was counted in — the defect CWS-890 was opened for.
   * @example
   * buildSharedOrderParams({ filterBy: 'today' })
   * { location_id: 'loc-1', filter_by: 'today' }
   * @param {OrderDateWindow | null} dateWindow - Resolved date window, or null for no date filter.
   * @param {{ includeLocation?: boolean, includeSearch?: boolean }} options -
   *   `includeLocation: false` keeps a request company-wide and `includeSearch: false` keeps it
   *   unfiltered by the searchbar; the CSV export uses both to preserve its established,
   *   unscoped extract, and the statistics request uses `includeSearch: false` because the
   *   searchbar sits below the cards and governs only the table.
   * @returns {Record<string, string>} Params ready to merge into an endpoint-specific param map.
   */
  private buildSharedOrderParams(
    dateWindow: OrderDateWindow | null,
    options: { includeLocation?: boolean; includeSearch?: boolean } = {}
  ): Record<string, string> {
    const params: Record<string, string> = {};

    if (options.includeLocation !== false && this.selectedLocationId) {
      params['location_id'] = this.selectedLocationId;
    }

    // Only the callers that opt in. The searchbar is the one control on this page that does not
    // govern the cards, and it is placed below them for exactly that reason.
    if (options.includeSearch !== false && this.searchTerm) {
      params['search'] = this.searchTerm;
    }

    if (dateWindow?.filterBy) {
      params['filter_by'] = dateWindow.filterBy;
      if (dateWindow.startDate && dateWindow.endDate) {
        params['start_date'] = dateWindow.startDate;
        params['end_date'] = dateWindow.endDate;
      }
    }

    return params;
  }

  /**
   * Resolves the filter-modal selection into a server-owned preset name. Only a custom range
   * carries dates, and those are the plain calendar dates the user picked.
   * @example
   * resolveFilterModalDateWindow()
   * { filterBy: 'yesterday' }
   * @returns {OrderDateWindow | null} Date window, or null when no usable date filter is applied.
   */
  private resolveFilterModalDateWindow(): OrderDateWindow | null {
    const filterBy = this.appliedFilters.dateRange?.filterBy;
    if (!filterBy || !FILTER_MODAL_DATE_PRESETS.has(filterBy)) {
      return null;
    }

    if (filterBy !== 'custom') {
      return { filterBy };
    }

    const { startDate, endDate } = this.appliedFilters.dateRange;
    if (!startDate || !endDate) {
      return null;
    }

    return {
      filterBy: 'custom',
      startDate: this.formatDateForAPI(new Date(startDate)),
      endDate: this.formatDateForAPI(new Date(endDate)),
    };
  }

  /**
   * Resolves the CSV dropdown selection into a server-owned preset name.
   * @example
   * resolveCsvDateWindow()
   * { filterBy: 'this_week' }
   * @returns {OrderDateWindow | null} Date window, or null for "All Orders" and unusable selections.
   */
  private resolveCsvDateWindow(): OrderDateWindow | null {
    const preset = CSV_OPTION_DATE_PRESETS[this.selectedCsvDownloadOption];
    if (!preset) {
      return null;
    }

    if (preset !== 'custom') {
      return { filterBy: preset };
    }

    if (!this.csvStartDate || !this.csvEndDate) {
      return null;
    }

    return {
      filterBy: 'custom',
      startDate: this.formatDateForAPI(this.csvStartDate),
      endDate: this.formatDateForAPI(this.csvEndDate),
    };
  }

  /**
   * Load order statistics for the currently selected company and update the component's orderStats.
   * Sends the same location, date window and filter-modal filters as the order list so the cards
   * match the table.
   *
   * The searchbar is deliberately left out. It sits below the cards and governs the table alone, so
   * sending it here would re-scope seven cards from a control the reader has to scroll past them to
   * reach — and svc-admin-panel discards it anyway, so today it would only cost a wasted request.
   * @example
   * this.loadOrderStats()
   * // no return value; this.orderStats will be updated asynchronously if the API responds successfully
   * @returns {void} Void; performs asynchronous API call and updates component state or returns early if no company is selected.
   */
  private loadOrderStats(): void {
    if (!this.selectedCompanyId) {
      return;
    }

    const params: Record<string, any> = {
      ...this.buildSharedOrderParams(this.resolveFilterModalDateWindow(), { includeSearch: false }),
      ...this.buildFilterModalParams(),
    };

    this.apiService
      .getWithSuperAdminToken<ApiOrderStatistics>(
        `admin/super-admin/companies/${this.selectedCompanyId}/orders/statistics`,
        params
      )
      .subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.applyStatisticsContext(response.data);
            this.orderStats = mapAdminDashboardPeriodStats(response.data as ApiOrderStatistics);
          }
        },
        error: error => {
          console.error('Error loading order statistics:', error);
        },
      });
  }

  /**
   * Records the non-card half of the statistics response: the zone(s) the figures were bucketed
   * in, and the lifetime totals the band carries. Kept apart from the cards on purpose — lifetime
   * figures rendered as cards is exactly how a lifetime number passed for a daily one here.
   * @example
   * applyStatisticsContext({ resolved_timezone: 'America/Los_Angeles', lifetime_orders: 7650 })
   * undefined
   * @param {ApiOrderStatistics} data - `data` block of the statistics response.
   * @returns {void} No return value; updates the timezone and lifetime state the band reads.
   */
  private applyStatisticsContext(data: ApiOrderStatistics): void {
    const context = data as {
      resolved_timezone?: string | null;
      timezones?: string[] | null;
      lifetime_orders?: number | null;
      lifetime_revenue?: number | null;
    };

    this.statsResolvedTimezone = context.resolved_timezone ?? '';
    this.statsTimezones = Array.isArray(context.timezones)
      ? [...new Set(context.timezones.filter(zone => typeof zone === 'string' && zone !== ''))]
      : [];
    this.lifetimeOrders =
      typeof context.lifetime_orders === 'number' ? context.lifetime_orders : null;
    this.lifetimeRevenue =
      typeof context.lifetime_revenue === 'number' ? context.lifetime_revenue : null;
  }

  /**
   * Applies a new search term to the table alone.
   *
   * The cards are NOT reloaded. Search is the one control on this page that does not govern the
   * scope named above the grid, which is why it lives with the table rather than in the scope
   * toolbar; reloading the cards from here would make that placement a lie.
   *
   * No debounce here on purpose: `app-searchbar` already emits `searchChange` through
   * `debounceTime` + `distinctUntilChanged`, so one settled keystroke run produces one request.
   * @example
   * onSearchChange('jane@example.com')
   * @param {string} searchTerm - Debounced term emitted by the searchbar.
   * @returns {void} No return value; reloads the order list only.
   */
  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadOrders();
  }

  onFilter(): void {
    // Sync filterCategories with appliedFilters before opening modal
    this.syncFilterCategoriesWithAppliedFilters();
    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
    this.showFilterModal = true;
  }

  /**
   * Synchronizes the modal's filter category options with the current appliedFilters. The date
   * scope is not among them: it is owned by the toolbar dropdown, not by this modal.
   * @example
   * syncFilterCategoriesWithAppliedFilters()
   * void
   * @returns {void} No return value.
   */
  private syncFilterCategoriesWithAppliedFilters(): void {
    // Sync carWashType
    const carWashTypeCategory = this.filterCategories.find(cat => cat.id === 'carWashType');
    if (carWashTypeCategory) {
      carWashTypeCategory.options.forEach(option => {
        if (option.value === 'monthlyMembership') {
          option.selected = this.appliedFilters.carWashType.monthlyMembership;
        } else if (option.value === 'singleWash') {
          option.selected = this.appliedFilters.carWashType.singleWash;
        } else if (option.value === 'prepaidWash') {
          option.selected = this.appliedFilters.carWashType.prepaidWash;
        } else if (option.value === 'rewash') {
          option.selected = this.appliedFilters.carWashType.rewash;
        }
      });
    }

    // Sync status
    const statusCategory = this.filterCategories.find(cat => cat.id === 'status');
    if (statusCategory) {
      statusCategory.options.forEach(option => {
        if (option.value === 'completed') {
          option.selected = this.appliedFilters.status.completed;
        } else if (option.value === 'scheduled') {
          option.selected = this.appliedFilters.status.scheduled;
        } else if (option.value === 'canceled') {
          option.selected = this.appliedFilters.status.canceled;
        } else if (option.value === 'inProgress') {
          option.selected = this.appliedFilters.status.inProgress;
        }
      });
    }

    // Sync source
    const sourceCategory = this.filterCategories.find(cat => cat.id === 'source');
    if (sourceCategory) {
      sourceCategory.options.forEach(option => {
        if (option.value === 'wayCom') {
          option.selected = this.appliedFilters.source.wayCom;
        } else if (option.value === 'scanAndGo') {
          option.selected = this.appliedFilters.source.scanAndGo;
        } else if (option.value === 'wayApp') {
          option.selected = this.appliedFilters.source.wayApp;
        } else if (option.value === 'website') {
          option.selected = this.appliedFilters.source.website;
        }
      });
    }

    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
  }

  onFilterOptionChange(event: { categoryId: string }): void {
    if (event.categoryId !== 'carWashType') {
      return;
    }
    this.refreshPackageFilterOptions();
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
  }

  /**
   * Default scope for a fresh page load. The date window opens on `today` rather than unset: an
   * unfiltered first paint greets the operator with lifetime figures under no period label at all,
   * which is the confusion this screen is being restructured to remove.
   * @example
   * createDefaultFilters().dateRange.filterBy
   * 'today'
   * @returns {any} Filter bag with the date scope defaulted to today.
   */
  private createDefaultFilters(): any {
    return {
      dateRange: {
        filterBy: 'today',
        startDate: null,
        endDate: null,
      },
      ...createDefaultAdminOrderFilters(),
    };
  }

  /**
   * Filter bag carrying the modal's own categories at their defaults while keeping whatever date
   * scope the toolbar currently shows. The modal no longer owns the date window, so neither
   * applying nor clearing it may move the period the band announces.
   * @example
   * createModalDefaultFilters().dateRange.filterBy
   * 'yesterday'
   * @returns {any} Filter bag with modal categories reset and the date scope preserved.
   */
  private createModalDefaultFilters(): any {
    return {
      ...createDefaultAdminOrderFilters(),
      dateRange: { ...this.appliedFilters.dateRange },
    };
  }

  /**
   * Apply the modal's wash type, status, source and package selections, then reload the table and
   * the cards. The date scope is untouched: it is owned by the toolbar dropdown that names the
   * band, so a modal apply can never silently re-period the page.
   * @example
   * onFilterModalApply([{ id: 'status', options: [{ value: 'completed', selected: true }] }])
   * undefined
   * @param {FilterCategory[]} categories - Filter categories whose selected options are applied.
   * @returns {void} No return value; updates this.appliedFilters, hides the modal and reloads.
   */
  onFilterModalApply(categories: FilterCategory[]): void {
    const filters: any = this.createModalDefaultFilters();

    categories.forEach(category => {
      const selectedOptions = category.options.filter(option => option.selected);

      switch (category.id) {
        case 'carWashType':
          selectedOptions.forEach(option => {
            if (option.value === 'monthlyMembership') filters.carWashType.monthlyMembership = true;
            if (option.value === 'singleWash') filters.carWashType.singleWash = true;
            if (option.value === 'prepaidWash') filters.carWashType.prepaidWash = true;
            if (option.value === 'rewash') filters.carWashType.rewash = true;
          });
          break;
        case 'status':
          selectedOptions.forEach(option => {
            if (option.value === 'completed') filters.status.completed = true;
            if (option.value === 'inProgress') filters.status.inProgress = true;
            if (option.value === 'scheduled') filters.status.scheduled = true;
            if (option.value === 'canceled') filters.status.canceled = true;
          });
          break;
        case 'source':
          selectedOptions.forEach(option => {
            if (option.value === 'wayCom') filters.source.wayCom = true;
            if (option.value === 'scanAndGo') filters.source.scanAndGo = true;
            if (option.value === 'wayApp') filters.source.wayApp = true;
            if (option.value === 'website') filters.source.website = true;
          });
          break;
        case 'package':
          filters.packageIds = selectedOptions.map(option => option.value);
          break;
      }
    });

    this.appliedFilters = filters;
    this.currentPage = 1;
    this.showFilterModal = false;
    this.loadOrders();
    this.loadOrderStats();
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  /**
   * Count behind the Filter button's badge. The date scope is excluded: it is always set and it is
   * already spelled out in its own dropdown and on the band, so counting it would leave the badge
   * permanently lit and tell the reader nothing.
   * @example
   * getActiveFilterCount()
   * 2
   * @returns {number} Number of active selections inside the filter modal.
   */
  getActiveFilterCount(): number {
    return (
      countActiveFilterValues(
        this.appliedFilters.carWashType,
        this.appliedFilters.status,
        this.appliedFilters.source
      ) + (this.appliedFilters.packageIds?.length ?? 0)
    );
  }

  get hasSearchOrFilters(): boolean {
    return (!!this.searchTerm && this.searchTerm.trim().length > 0) || this.hasActiveFilters;
  }

  get paginationData(): PaginationData {
    return buildPaginationData(
      this.currentPage,
      this.totalPages,
      this.totalItems,
      this.itemsPerPage
    );
  }

  get totalPages(): number {
    return calculateTotalPages(this.totalItems, this.itemsPerPage);
  }

  onPageChange(page: number): void {
    applyPageChange(
      {
        totalPages: this.totalPages,
        setCurrentPage: p => {
          this.currentPage = p;
        },
        loadOrders: () => this.loadOrders(),
      },
      page
    );
  }

  onItemsPerPageChange(newItemsPerPage: number): void {
    applyItemsPerPageChange(
      {
        setItemsPerPage: v => {
          this.itemsPerPage = v;
        },
        setCurrentPage: p => {
          this.currentPage = p;
        },
        loadOrders: () => this.loadOrders(),
      },
      newItemsPerPage
    );
  }

  toggleDropdown(event: Event, orderId: string): void {
    event.stopPropagation();

    if (this.openDropdownId === orderId) {
      this.closeDropdown();
    } else {
      this.openDropdown(event, orderId);
    }
  }

  private closeDropdown(): void {
    this.openDropdownId = null;
    this.selectedOrderForDropdown = null;
  }

  /**
   * Opens the actions dropdown for a specific order and positions it relative to the event target.
   * @example
   * openDropdown(event, 'order-123')
   * undefined
   * @param {Event} event - The DOM event from the click that triggered opening the dropdown.
   * @param {string} orderId - The id of the order to select and display the dropdown for.
   * @returns {void} No return value.
   */
  private openDropdown(event: Event, orderId: string): void {
    this.openDropdownId = orderId;
    this.selectedOrderForDropdown = this.orders.find(order => order.id === orderId) || null;

    setTimeout(() => {
      const button = event.target as HTMLElement;
      const dropdown = document.querySelector('.actions-dropdown-menu') as HTMLElement;
      if (button && dropdown) {
        const rect = button.getBoundingClientRect();
        dropdown.style.top = `${rect.bottom + 5}px`;
        dropdown.style.left = `${rect.right - dropdown.offsetWidth}px`;
      }
    }, 0);
  }

  viewOrder(order: Order): void {
    this.closeDropdown();
    openOrderDetailsModal(this, order);
  }

  cancelOrder(order: Order): void {
    this.closeDropdown();
    this.orderToCancel = order;
    this.showCancelOrderModal = true;
  }

  /**
   * Initiates the cancellation confirmation flow for a given order and handles success callbacks to update UI and reload data.
   * @example
   * nCancelOrderConfirm({ order: sampleOrder, reason: 'Customer requested cancellation', action: 'cancel' })
   * undefined
   * @param {{ order: Order; reason: string; action?: string }} {{data}} - Data object containing the order to cancel, a reason for the cancellation, and an optional action flag.
   * @returns {{void}} No return value; performs cancellation confirmation and updates UI/state as needed.
   **/
  onCancelOrderConfirm(data: { order: Order; reason: string; action?: string }): void {
    runCancelOrderConfirm(data, this.cancellationService, this.messageService, {
      onSuccess: (wasScheduled, refundAmount) => {
        this.cancellationWasScheduled = wasScheduled;
        this.cancellationRefundAmount = refundAmount;
        this.showCancellationSuccessModal = true;
      },
      loadOrders: () => this.loadOrders(),
      loadOrderStats: () => this.loadOrderStats(),
      closeModal: () => this.closeCancelOrderModal(),
    });
  }

  closeCancelOrderModal(): void {
    this.showCancelOrderModal = false;
    this.orderToCancel = null;
  }

  /**
   * Handle document click events to close dropdowns (including CSV download) when clicking outside, while ignoring clicks inside action elements or visible calendar panels and detached nodes.
   * @example
   * onDocumentClick(new MouseEvent('click'))
   * undefined
   * @param {{Event}} {{event}} - The DOM click event triggered on the document.
   * @returns {{void}} No return value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target.closest('.actions')) {
      this.closeDropdown();
    }

    // Material datepicker overlay (panelClass + default popup classes).
    // Legacy PrimeNG selectors kept so older markup does not close the menu early.
    const allCalendarPanels = document.querySelectorAll(
      [
        '.csv-calendar-panel',
        '.mat-datepicker-content',
        '.mat-datepicker-popup',
        '.cdk-overlay-pane:has(.mat-datepicker-content)',
        '.p-datepicker-panel',
        '.p-datepicker',
      ].join(', ')
    );
    const visibleCalendarPanels = Array.from(allCalendarPanels).filter(panel => {
      const style = window.getComputedStyle(panel);
      const rect = panel.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        rect.width > 0 &&
        rect.height > 0
      );
    });

    // Check if target is inside a visible calendar panel
    // Also check if the target is detached from DOM (often happens when navigating calendar months)
    const isDetached = !document.body.contains(target);
    const isInsideVisibleCalendarPanel = visibleCalendarPanels.some(panel =>
      panel.contains(target)
    );

    // Close CSV download dropdown if clicking outside (but not if clicking inside calendar or dropdown container)
    const csvDropdownContainer = target.closest('.csv-download-dropdown-container');
    const csvDropdownMenu = target.closest('.csv-download-dropdown-menu');

    // Only close if dropdown is open, click is outside container, and not inside visible calendar panel
    // We also check isCalendarOpen flag and isDetached to handle navigation clicks
    if (
      this.isCsvDownloadDropdownOpen &&
      !csvDropdownContainer &&
      !csvDropdownMenu &&
      !isInsideVisibleCalendarPanel &&
      !isDetached &&
      !this.isCalendarOpen
    ) {
      this.closeCsvDownloadDropdown();
    }
  }

  /**
   * Sorts the component's orders array by the orderId field and updates the sort state.
   * @example
   * ortByOrderId()
   * // returns void
   * @returns {void} Updates this.orders, this.sortColumn, and this.sortDirection in-place.
   */
  sortByOrderId(): void {
    const result = TableUtils.applyOrdersSort(
      this.orders,
      this.sortColumn ?? '',
      this.sortDirection,
      'orderId',
      (a, b) => a.orderId.localeCompare(b.orderId)
    );
    this.orders = result.sortedOrders;
    this.sortColumn = result.sortColumn;
    this.sortDirection = result.sortDirection as 'asc' | 'desc' | 'none';
  }

  /**
   * Sorts the component's orders by their dateTime values and updates the component's sortColumn and sortDirection state.
   * @example
   * ortByDateTime()
   * void
   * @returns {{void}} No direct return value; the function mutates this.orders, this.sortColumn, and this.sortDirection.
   **/
  sortByDateTime(): void {
    const result = TableUtils.applyOrdersSort(
      this.orders,
      this.sortColumn ?? '',
      this.sortDirection,
      'dateTime',
      (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    );
    this.orders = result.sortedOrders;
    this.sortColumn = result.sortColumn;
    this.sortDirection = result.sortDirection as 'asc' | 'desc' | 'none';
  }

  getSortIcon(column: string): string {
    return TableUtils.getSortIcon(this.sortColumn, this.sortDirection, column);
  }

  openOrderDetails(order: Order): void {
    openOrderDetailsModal(this, order);
  }

  closeOrderDetails(): void {
    closeOrderDetailsModal(this);
  }

  onOrderCancelled(_cancelledOrder: Order): void {
    this.loadOrders();
    this.closeOrderDetails();
  }

  onBookAgainFromOrderDetails(): void {
    // Close order details modal
    this.closeOrderDetails();
    // Could navigate to add order or show message
  }

  onCancellationSuccessClose(): void {
    this.showCancellationSuccessModal = false;
  }

  onCancellationBookAgain(): void {
    this.showCancellationSuccessModal = false;
    // Could navigate to add order or show message
  }

  canCancelOrder(order: Order): boolean {
    return OrderUtils.canBeCancelled(order);
  }

  canShowActions(_order: Order): boolean {
    return true;
  }

  getStatusClass(status: OrderStatus): string {
    return `status-${status.color}`;
  }

  getWashTypeClass(washType: string, washTypeValue?: string): string {
    const color = OrderUtils.getWashTypeColor(washTypeValue || washType);
    if (color) {
      return `wash-type-${color}`;
    }
    if (washType === 'Single Wash' || washType === 'Rewash - Single Wash') {
      return 'wash-type-orange';
    }
    if (washType === 'Monthly Membership' || washType === 'Rewash - Monthly Membership') {
      return 'wash-type-purple';
    }
    return 'wash-type-default';
  }

  getCapitalizedSource(source: string | undefined): string {
    return OrderUtils.capitalizeSource(source);
  }

  formatPaymentMethod(paymentMethod: string | undefined): string {
    return OrderUtils.formatPaymentMethod(paymentMethod);
  }

  /**
   * Timezone the reporting window was bucketed by, for on-screen auditing. Read solely from the
   * statistics response: the selected location's own zone is NOT it when the server buckets every
   * row in one shared zone, and deriving it here would make the label assert a zone no figure on
   * the page was cut in — worse than showing no label, which the template's `*ngIf` handles.
   * @example
   * resolvedTimezone
   * 'America/Los_Angeles'
   * @returns {string} IANA timezone name, or an empty string when the server reported none
   *   (no response yet, or a window genuinely spanning several zones).
   */
  get resolvedTimezone(): string {
    return this.statsResolvedTimezone;
  }

  /**
   * Whether the figures on screen were bucketed in more than one zone. A single label would then
   * be a lie, so the scope line, the band and the Date & Time header all degrade together.
   * @example
   * hasMultipleTimezones
   * false
   * @returns {boolean} True when the statistics response reported several distinct zones.
   */
  get hasMultipleTimezones(): boolean {
    return this.statsTimezones.length > 1;
  }

  // ---------------------------------------------------------------------------------------------
  // Date scope — the one control that governs both the cards and the table.
  // ---------------------------------------------------------------------------------------------

  toggleDateScopeDropdown(): void {
    this.isDateScopeDropdownOpen = !this.isDateScopeDropdownOpen;
  }

  closeDateScopeDropdown(): void {
    this.isDateScopeDropdownOpen = false;
  }

  /**
   * Selects the period every card and every row on the page describes. A preset drops any custom
   * dates so a stale range cannot survive underneath a preset label.
   * @example
   * selectDateScope('yesterday')
   * undefined
   * @param {string} value - Date scope value, or `all` for no date filter.
   * @returns {void} No return value; reloads the order list and the statistics cards.
   */
  selectDateScope(value: string): void {
    this.dateFilterBy = value || ALL_TIME_DATE_VALUE;
    this.isDateScopeDropdownOpen = false;

    if (this.dateFilterBy !== 'custom') {
      this.dateStartDate = null;
      this.dateEndDate = null;
    }

    this.applyDateScope();
  }

  /**
   * Applies a custom range once both ends are picked. A half-filled range is not an error worth a
   * toast here — the dropdown stays open on `Custom Range` until the second date arrives.
   * @example
   * onCustomRangeDateChange()
   * undefined
   * @returns {void} No return value; reloads only when the range is complete.
   */
  onCustomRangeDateChange(): void {
    if (this.dateStartDate && this.dateEndDate) {
      this.applyDateScope();
    }
  }

  /**
   * Pushes the toolbar's date scope into the applied filters and reloads both halves of the page.
   * @example
   * applyDateScope()
   * undefined
   * @returns {void} No return value; leaves state untouched for an incomplete custom range.
   */
  private applyDateScope(): void {
    if (this.dateFilterBy === 'custom' && (!this.dateStartDate || !this.dateEndDate)) {
      return;
    }

    this.appliedFilters.dateRange = {
      filterBy: this.dateFilterBy,
      startDate: this.dateStartDate,
      endDate: this.dateEndDate,
    };
    this.currentPage = 1;
    this.loadOrders();
    this.loadOrderStats();
  }

  /** Date scope currently in force, falling back to All time when nothing is applied. */
  private get activeDateValue(): string {
    return this.appliedFilters.dateRange?.filterBy || ALL_TIME_DATE_VALUE;
  }

  /** True when the page is showing every order ever, so lifetime totals would be said twice. */
  get isAllTimeScope(): boolean {
    return this.activeDateValue === ALL_TIME_DATE_VALUE;
  }

  // ---------------------------------------------------------------------------------------------
  // Band and column header — where the page says which time frame it is showing.
  //
  // There is deliberately no scope line and no viewer-mismatch notice. The company and the location
  // are already named by the two dropdowns that select them, and a sentence comparing the viewer's
  // zone to the reporting zone read as a tautology whenever the two agreed. The band and the
  // `Date & Time (…)` column header carry the zone context instead.
  // ---------------------------------------------------------------------------------------------

  /**
   * Left half of the band: the period this page is showing, the calendar day when the period is
   * one day, and the zone that day was cut in.
   * @example
   * bandPeriodLabel
   * 'Yesterday · Aug 13 · Pacific Time'
   * @returns {string} Period label, dropping any part the page cannot state truthfully.
   */
  get bandPeriodLabel(): string {
    const scope = this.activeDateValue;
    const parts: string[] = [];

    if (scope === 'custom') {
      parts.push(this.customRangeLabel() || DATE_SCOPE_BAND_LABELS['custom']);
    } else {
      parts.push(DATE_SCOPE_BAND_LABELS[scope] ?? DATE_SCOPE_BAND_LABELS[ALL_TIME_DATE_VALUE]);
      const day = this.singleDayLabel(scope);
      if (day) {
        parts.push(day);
      }
    }

    if (this.hasMultipleTimezones) {
      parts.push(MULTI_ZONE_BAND_LABEL);
    } else {
      // Same degradation as the scope line: an unusable zone drops the zone half of the band
      // rather than leaving a dangling separator where its name should be.
      const zoneName = formatZoneLong(this.statsResolvedTimezone);
      if (zoneName) {
        parts.push(zoneName);
      }
    }

    return parts.join(' · ');
  }

  /**
   * Whether the band's lifetime half should appear. Hidden on an All time page: repeating the same
   * number under a second heading is what made two scopes look like one.
   * @example
   * showLifetimeTotals
   * true
   * @returns {boolean} True when a bounded period is selected and lifetime figures have arrived.
   */
  get showLifetimeTotals(): boolean {
    return !this.isAllTimeScope && (this.lifetimeOrders !== null || this.lifetimeRevenue !== null);
  }

  /**
   * Right half of the band: totals across every order ever, for the same company and location.
   * Deliberately not a card — identical card styling across two time frames is what hid the bug.
   * @example
   * lifetimeSummary
   * 'All time · 7,650 orders · $130,030.86'
   * @returns {string} Muted lifetime summary, omitting whichever figure the API did not report.
   */
  get lifetimeSummary(): string {
    const parts: string[] = ['All time'];

    if (this.lifetimeOrders !== null) {
      const orders = new Intl.NumberFormat('en-US').format(this.lifetimeOrders);
      parts.push(`${orders} ${this.lifetimeOrders === 1 ? 'order' : 'orders'}`);
    }

    if (this.lifetimeRevenue !== null) {
      parts.push(
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
          this.lifetimeRevenue
        )
      );
    }

    return parts.join(' · ');
  }

  /**
   * The lifetime figures as value/label pairs so the template can weight the numbers and mute the
   * words. Rendered as one run-on string they were unreadable: the separators carried the same
   * weight as the figures, so nothing showed the reader where a number started.
   *
   * `lifetimeSummary` remains the flat string and is used as the accessible label, so assistive
   * technology hears one sentence rather than disconnected fragments.
   * @example
   * lifetimeFigures
   * [{ value: '7,650', label: 'orders' }, { value: '$130,030.86', label: 'all-time revenue' }]
   * @returns {{value: string, label: string}[]} Figures the API reported, in display order.
   */
  get lifetimeFigures(): { value: string; label: string }[] {
    const figures: { value: string; label: string }[] = [];

    if (this.lifetimeOrders !== null) {
      figures.push({
        value: new Intl.NumberFormat('en-US').format(this.lifetimeOrders),
        label: this.lifetimeOrders === 1 ? 'order' : 'orders',
      });
    }

    if (this.lifetimeRevenue !== null) {
      figures.push({
        value: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(this.lifetimeRevenue),
        label: 'revenue',
      });
    }

    return figures;
  }

  /**
   * Zone suffix for the Date & Time column header, so a reader never has to assume whose clock a
   * row is on.
   * @example
   * dateColumnZoneLabel
   * 'PT'
   * @returns {string} Short zone name, `local` across several zones, or empty when unknown.
   */
  get dateColumnZoneLabel(): string {
    if (this.hasMultipleTimezones) {
      return MULTI_ZONE_COLUMN_LABEL;
    }
    return formatZoneShort(this.statsResolvedTimezone);
  }

  /**
   * IANA name(s) behind the column header's short label, for auditing via the `title` attribute.
   * @example
   * dateColumnZoneTitle
   * 'America/Los_Angeles'
   * @returns {string} Comma-separated IANA zone names, or an empty string when none are known.
   */
  get dateColumnZoneTitle(): string {
    if (this.hasMultipleTimezones) {
      return this.statsTimezones.join(', ');
    }
    return this.statsResolvedTimezone;
  }

  /** Placeholder that tells the searchbar's honest scope: these rows, not the cards above. */
  get searchPlaceholder(): string {
    const count = new Intl.NumberFormat('en-US').format(this.totalItems);
    return this.totalItems === 1
      ? 'Search this 1 order by order ID, customer name, email...'
      : `Search these ${count} orders by order ID, customer name, email...`;
  }

  /**
   * Spells out the single calendar day a `today` or `yesterday` scope covers, in the zone the
   * server says it bucketed by. Derived from that zone rather than the browser's so the label and
   * the figures can never name different days; omitted entirely across several zones, where no one
   * day is true of every figure.
   * @example
   * singleDayLabel('yesterday')
   * 'Aug 13'
   * @param {string} scope - Active date scope value.
   * @returns {string} Formatted day, or an empty string when the scope spans more than one day.
   */
  private singleDayLabel(scope: string): string {
    if (!SINGLE_DAY_DATE_SCOPES.has(scope)) {
      return '';
    }
    if (this.hasMultipleTimezones) {
      return '';
    }

    const offsetDays = scope === 'yesterday' ? -1 : 0;

    // Through the guarded helper rather than `Intl` directly: `resolved_timezone` is free text from
    // the locations table, so a legacy 'PDT', a typo or an empty column reaches this binding
    // intact, and an unguarded formatter raising a RangeError during change detection blanks the
    // whole page instead of degrading one label. An unusable zone falls back to the plain calendar
    // day on the browser's own clock; the band drops the zone name in the same pass, so a plain
    // date is never presented as a date in a zone this page cannot name.
    return (
      calendarDayInZone(this.statsResolvedTimezone, offsetDays) ||
      calendarDayInZone(getViewerZone(), offsetDays)
    );
  }

  /**
   * Period the empty state names, phrased to sit in the middle of a sentence. Empty on an All time
   * page, where no window is excluding anything and the emptiness really is about the company.
   * @example
   * emptyStatePeriodPhrase
   * 'today'
   * @returns {string} Period phrase, or an empty string when no date window is in force.
   */
  private get emptyStatePeriodPhrase(): string {
    const scope = this.activeDateValue;
    if (scope === 'custom') {
      return this.customRangeLabel() || 'the selected range';
    }
    return DATE_SCOPE_EMPTY_STATE_PHRASES[scope] ?? '';
  }

  /**
   * Empty-state copy for the no-orders case. With the date scope defaulting to Today the common
   * empty result is "nothing yet in this window", not "this company has never had an order", and
   * blaming the company for a result the window produced points the reader at the wrong control.
   * @example
   * emptyStateDescription
   * 'No orders for today at this company and location.'
   * @returns {string} Description naming the selected period, or the company when showing all time.
   */
  get emptyStateDescription(): string {
    const phrase = this.emptyStatePeriodPhrase;
    return phrase
      ? `No orders for ${phrase} at this company and location.`
      : 'No orders found for the selected company.';
  }

  /**
   * Whether the empty state may offer to widen the window. Hidden on an All time page, where the
   * action would do nothing while implying there is still something left to try.
   * @example
   * canWidenToAllTime
   * true
   * @returns {boolean} True while a bounded period is selected.
   */
  get canWidenToAllTime(): boolean {
    return !this.isAllTimeScope;
  }

  /**
   * Widens the page to every order ever, from the empty state's action. Routed through the same
   * entry point as the toolbar dropdown so the band, the cards and the table move together and the
   * dropdown does not keep showing a period the page is no longer scoped to.
   * @example
   * viewAllTime()
   * undefined
   * @returns {void} No return value; reloads the order list and the statistics cards.
   */
  viewAllTime(): void {
    this.selectDateScope(ALL_TIME_DATE_VALUE);
  }

  /**
   * Renders the applied custom range as plain dates, because "Custom Range" alone tells a reader
   * nothing about which days the figures cover.
   * @example
   * customRangeLabel()
   * 'Jan 5 – Jan 9, 2026'
   * @returns {string} Formatted range, or an empty string when either end is missing.
   */
  private customRangeLabel(): string {
    const { startDate, endDate } = this.appliedFilters.dateRange ?? {};
    if (!startDate || !endDate) {
      return '';
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const shortDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    const fullDay = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${shortDay.format(start)} – ${fullDay.format(end)}`;
  }

  private formatDateForAPI(date: Date): string {
    return formatDateToISO(date);
  }

  // CSV Download methods
  toggleCsvDownloadDropdown(): void {
    this.isCsvDownloadDropdownOpen = !this.isCsvDownloadDropdownOpen;
  }

  closeCsvDownloadDropdown(): void {
    this.isCsvDownloadDropdownOpen = false;
  }

  onCalendarShow(event: any): void {
    this.isCalendarOpen = true;
    // Prevent dropdown from closing when calendar opens
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
  }

  onCalendarHide(event: any): void {
    // Use setTimeout to ensure the calendar panel is fully removed from DOM
    setTimeout(() => {
      this.isCalendarOpen = false;
    }, 0);
  }

  /**
   * Selects a CSV download date option. Presets are named windows resolved by the server, so only
   * a custom range keeps client-side dates.
   * @example
   * electCsvDownloadOption('thisWeek')
   * undefined
   * @param {{string}} {{option}} - The selected CSV date option (e.g., 'custom', 'all', or a preset key).
   * @returns {{void}} Does not return a value; updates component state (selectedCsvDownloadOption, csvStartDate, csvEndDate).
   **/
  selectCsvDownloadOption(option: string): void {
    this.selectedCsvDownloadOption = option || '';
    // Don't close dropdown when selecting - keep it open to show date inputs
    if (this.selectedCsvDownloadOption !== 'custom') {
      this.csvStartDate = null;
      this.csvEndDate = null;
    }
  }

  getCsvDownloadLabel(): string {
    if (!this.selectedCsvDownloadOption) {
      return 'Download CSV';
    }
    const option = this.csvDownloadOptions.find(
      opt => opt.value === this.selectedCsvDownloadOption
    );
    return option?.label ?? 'Download CSV';
  }

  onCsvDownloadApply(): void {
    // Close dropdown and download CSV
    this.isCsvDownloadDropdownOpen = false;
    this.downloadCsv();
  }

  /**
   * Initiates download of a CSV file containing completed orders for the currently selected company, validating company selection and date inputs based on the chosen download option ('all', 'custom', or preset ranges) and then delegating to the CSV download helper.
   * @example
   * this.downloadCsv()
   * undefined
   * @returns {{void}} Does not return a value; triggers file download or displays validation/warning messages.
   */
  downloadCsv(): void {
    if (!this.selectedCompanyId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a company first',
      });
      return;
    }

    // Validate dates for custom range
    if (this.selectedCsvDownloadOption === 'custom') {
      if (!this.csvStartDate || !this.csvEndDate) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select both start date and end date for custom range',
        });
        return;
      }
    }

    // No location_id and no search: this export has always covered the whole company, and
    // narrowing it to the auto-selected location or to whatever is typed in the searchbar would
    // silently drop rows from a finance extract.
    const params: Record<string, any> = {
      status: 'completed',
      ...this.buildSharedOrderParams(this.resolveCsvDateWindow(), {
        includeLocation: false,
        includeSearch: false,
      }),
    };
    // For "all orders", we don't need filter_by, start_date, or end_date
    // The API will return all completed orders

    // Build the URL
    const endpoint = `admin/super-admin/companies/${this.selectedCompanyId}/orders/download/csv`;

    // Download the CSV file
    this.downloadCsvFile(endpoint, params);
  }

  /**
   * Downloads a CSV file from the specified API endpoint using the stored super admin token and triggers a browser download.
   * @example
   * downloadCsvFile('orders/export', { companyId: '123' })
   * undefined
   * @param {{string}} {{endpoint}} - The API endpoint path (appended to the baseUrl) to request the CSV from.
   * @param {{Record<string, any>}} {{params}} - Query parameters to include in the request.
   * @returns {{void}} No return value; triggers file download and displays success or error messages.
   **/
  private downloadCsvFile(endpoint: string, params: Record<string, any>): void {
    // Get the super admin token
    const superAdminData = localStorage.getItem('superAdminData');
    if (!superAdminData) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Authentication token not found',
      });
      return;
    }

    try {
      const parsedData = JSON.parse(superAdminData);
      const token = parsedData?.token;

      if (!token) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Authentication token not found',
        });
        return;
      }

      // Construct headers
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
      });

      // Construct URL
      const url = `${environment.baseUrl}/${environment.apiVersion}/${endpoint}`;

      // Make HTTP request with blob response type
      this.http
        .get(url, {
          headers: headers,
          params: params,
          responseType: 'blob',
        })
        .subscribe({
          next: (blob: Blob) => {
            // Create a blob URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            const selectedCompany = this.companyOptions.find(
              opt => opt.value === this.selectedCompanyId
            );
            const companyName = selectedCompany
              ? selectedCompany.label.replace(/[^a-z0-9]/gi, '_').toLowerCase()
              : this.selectedCompanyId;
            link.download = `orders_list_${companyName}_${Date.now()}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'CSV file downloaded successfully',
            });
          },
          error: error => {
            console.error('Error downloading CSV:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to download CSV. Please try again.',
            });
          },
        });
    } catch (error) {
      console.error('Error parsing super admin data:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to download CSV. Please try again.',
      });
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): unknown {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): unknown {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): unknown {
    return s;
  }
}
